import { create } from 'zustand';
import { Customer } from '@/types';
import { authApi, extractResource } from '@/lib/api';
import { getToken, setToken, removeToken } from '@/lib/auth';
import { useCartStore } from '@/store/cartStore';
import { runSignOutResets } from '@/lib/storeResets';
import { registerPushToken, removePushToken } from '@/lib/native';

/** Extra /auth/me attempts on a network or server error before giving up for now. */
const CHECK_AUTH_RETRIES = 2;

/** The session check in progress, so overlapping triggers share one request. */
let checkInFlight: Promise<void> | null = null;

interface AuthState {
  customer: Customer | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, customer: Customer) => void;
  logout: () => Promise<void>;
  updateProfile: (customer: Customer) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  customer: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,

  login: (token: string, customer: Customer) => {
    setToken(token);
    set({ token, customer, isAuthenticated: true, isLoading: false });
    // Fire-and-forget: merge any guest-cart items the visitor accumulated before
    // signing in. Failures here shouldn't block login — the user can still
    // re-add items manually. Guards against test envs where the cart store
    // hasn't been initialised.
    try {
      void useCartStore.getState().mergeGuestCart();
    } catch {
      // ignore
    }
    // On the native app, register this device for order push notifications.
    // No-op on web; failures are swallowed inside registerPushToken.
    void registerPushToken();
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors on logout
    } finally {
      removeToken();
      // Stop this device receiving the prior user's pushes (native only).
      void removePushToken();
      // Wipe client-side caches that hung off the auth identity. Wrapped in
      // try/catch so a thrown reset can't block sign-out from completing.
      try {
        useCartStore.getState().clearCart();
      } catch {
        // ignore
      }
      // Reset per-user state owned by lazily-loaded stores (the design editor
      // registers its own reset). Importing designStore here would pull Konva
      // into the eager bundle on every page — see lib/storeResets.ts.
      runSignOutResets();
      set({ token: null, customer: null, isAuthenticated: false, isLoading: false });
    }
  },

  updateProfile: (customer: Customer) => {
    set({ customer });
  },

  checkAuth: () => {
    if (checkInFlight) return checkInFlight;
    checkInFlight = runCheckAuth(set).finally(() => {
      checkInFlight = null;
    });
    return checkInFlight;
  },
}));

/**
 * Validate the saved token with /auth/me. Only a 401 means the token is dead
 * (the api interceptor then clears it). A network blip or server error used to
 * delete the token too, signing the customer out on an ordinary refresh: those
 * are retried, and if the server stays unreachable the customer is treated as
 * signed out for now while the cookie is kept, so Providers can re-check when
 * the connection or the tab comes back.
 */
async function runCheckAuth(set: (partial: Partial<AuthState>) => void): Promise<void> {
  const token = getToken();
  if (!token) {
    set({ isLoading: false, isAuthenticated: false });
    return;
  }
  // A check that finishes after the customer signed in (or out) again must not
  // overwrite that newer state.
  const stale = () => getToken() !== token;

  for (let attempt = 0; ; attempt++) {
    try {
      const response = await authApi.me(true);
      if (stale()) return;
      set({
        token,
        customer: extractResource<Customer>(response.data, 'customer'),
        isAuthenticated: true,
        isLoading: false,
      });
      return;
    } catch (err) {
      if (stale()) return;
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        removeToken();
        set({ token: null, customer: null, isAuthenticated: false, isLoading: false });
        return;
      }
      const retryable = status === undefined || status >= 500;
      if (!retryable || attempt >= CHECK_AUTH_RETRIES) {
        set({ token: null, customer: null, isAuthenticated: false, isLoading: false });
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
}
